import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { signOut } from "firebase/auth";
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { Mail, CheckCircle, AlertCircle, Loader2, LogOut, RefreshCw, Clock } from "lucide-react";
import { useAuthContext } from "../../contexts/AuthContext";

export default function VerifyEmailPage() {
  const { refreshUser } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [hasPendingSalePagePayment, setHasPendingSalePagePayment] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-verify email if token is present in URL
  useEffect(() => {
    const verifyEmailFromToken = async () => {
      const token = searchParams.get('token');
      if (!token) return;

      try {
        console.log('[VerifyEmailPage] Token found, verifying email...');
        
        // Call Cloud Function to verify email
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions(auth.app, 'us-central1');
        const verifyEmailFunc = httpsCallable(functions, 'verifyEmailWithToken');
        
        const result = await verifyEmailFunc({ token }) as any;
        
        if (result.data.success) {
          console.log('[VerifyEmailPage] ✅ Email verified successfully');
          
          // Reload user to get updated emailVerified status from Firebase Auth
          if (auth.currentUser) {
            await auth.currentUser.reload();
            console.log('[VerifyEmailPage] Firebase Auth user reloaded, emailVerified:', auth.currentUser.emailVerified);
          }
          
          // Refresh AuthContext to update user state
          await refreshUser();
          console.log('[VerifyEmailPage] AuthContext refreshed');
          
          setSuccess(true);
          
          // Redirect after 1 second to คอร์สดี.com
          setTimeout(() => {
            window.location.href = 'https://aiforyou.web.app/';
          }, 1000);
        } else {
          setError('เกิดข้อผิดพลาดในการยืนยันอีเมล');
        }
      } catch (err: any) {
        console.error('[VerifyEmailPage] Error verifying email:', err);
        
        if (err.code === 'failed-precondition') {
          setError('ลิงก์ยืนยันหมดอายุแล้ว กรุณาขอลิงก์ใหม่');
        } else {
          setError('เกิดข้อผิดพลาดในการยืนยันอีเมล');
        }
      }
    };

    verifyEmailFromToken();
  }, [searchParams]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!auth.currentUser) return;
      
      try {
        // Check for course subscription payments
        const paymentsRef = collection(db, 'subscriptionPayments');
        const q = query(
          paymentsRef,
          where('userId', '==', auth.currentUser.uid),
          where('status', '==', 'pending')
        );
        const snapshot = await getDocs(q);
        
        // Check for salepage payments
        const salePagePaymentsRef = collection(db, 'salePagePaymentNotifications');
        const salePageQuery = query(
          salePagePaymentsRef,
          where('userId', '==', auth.currentUser.uid),
          where('status', '==', 'pending')
        );
        const salePageSnapshot = await getDocs(salePageQuery);
        
        const hasCoursePayment = !snapshot.empty;
        const hasSalePagePayment = !salePageSnapshot.empty;
        
        setHasPendingPayment(hasCoursePayment || hasSalePagePayment);
        setHasPendingSalePagePayment(hasSalePagePayment);
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        setCheckingPayment(false);
      }
    };
    
    checkPaymentStatus();
  }, []);

  useEffect(() => {
    const checkVerification = setInterval(async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          // Get user's signup source and redirect to landing page
          const userRef = doc(db, "users", auth.currentUser.uid);
          const userDoc = await getDoc(userRef);
          const signupSource = userDoc.exists() ? userDoc.data()?.signupSource : null;
          
          let redirectUrl = 'https://aiforyou.web.app/';
          if (signupSource === 'coursedee') {
            redirectUrl = 'https://aiforyou.web.app/';
          } else if (signupSource === 'salepage') {
            redirectUrl = 'https://saledi.web.app/';
          }
          
          console.log('[VerifyEmailPage] Email verified! Redirecting to:', redirectUrl);
          window.location.href = redirectUrl;
        }
      }
    }, 3000);

    return () => clearInterval(checkVerification);
  }, [navigate]);

  const handleResendEmail = async () => {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      setError("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (countdown > 0) {
      setError(`กรุณารอ ${countdown} วินาที ก่อนส่งอีกครั้ง`);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      // Get user's signupSource from Firestore
      const userRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userRef);
      const signupSource = userDoc.exists() ? userDoc.data()?.signupSource : null;
      const displayName = userDoc.exists() ? userDoc.data()?.displayName : 'ผู้ใช้';
      
      // Generate custom verification link
      const verifyToken = btoa(JSON.stringify({
        email: currentUser.email,
        uid: currentUser.uid,
        timestamp: Date.now()
      }));
      
      let verifyLink = `https://aiforyou.web.app/verify-email?token=${verifyToken}`;
      if (signupSource === 'coursedee') {
        verifyLink = `https://aiforyou.web.app/verify-email?token=${verifyToken}`;
      } else if (signupSource === 'salepage') {
        verifyLink = `https://saledi.web.app/verify-email?token=${verifyToken}`;
      }
      
      console.log('[VerifyEmailPage] Calling Cloudflare Worker directly...');
      const response = await fetch('https://edumi-verification-email.edumi-subdomin.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: currentUser.email,
          displayName: displayName,
          verifyLink: verifyLink
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('[VerifyEmailPage] ✅ Verification email sent via Cloudflare Worker');
        setSuccess(true);
        setCountdown(60);
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
      
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err: any) {
      console.error('[VerifyEmailPage] Error sending verification email:', err);
      
      let errorMessage = "ไม่สามารถส่งอีเมลยืนยันได้ กรุณาลองใหม่อีกครั้ง";
      
      if (err.code === 'auth/too-many-requests') {
        errorMessage = "ส่งอีเมลบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่";
        setCountdown(120);
      } else if (err.code === 'auth/user-token-expired') {
        errorMessage = "Session หมดอายุ กรุณาเข้าสู่ระบบใหม่";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const handleRefresh = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        try {
          const userRef = doc(db, "users", auth.currentUser.uid);
          const userDoc = await getDoc(userRef);
          const signupSource = userDoc.exists() ? userDoc.data()?.signupSource : null;
          
          let redirectUrl = 'https://aiforyou.web.app/';
          if (signupSource === 'coursedee') {
            redirectUrl = 'https://aiforyou.web.app/';
          } else if (signupSource === 'salepage') {
            redirectUrl = 'https://saledi.web.app/';
          }
          
          console.log('[VerifyEmailPage] Email verified! Redirecting to:', redirectUrl);
          window.location.href = redirectUrl;
        } catch (error) {
          console.error('[VerifyEmailPage] Error getting user source:', error);
          window.location.href = 'https://aiforyou.web.app/';
        }
      } else {
        setError("อีเลลยังไม่ได้รับการยืนยัน กรุณาตรวจสอบกล่องจดหมายของคุณ");
      }
    }
  };

  // If has token parameter, allow verification even without login
  const hasToken = searchParams.get('token');
  
  if (!auth.currentUser && !hasToken) {
    navigate("/login");
    return null;
  }

  // If verifying with token, show verification UI (regardless of login status)
  if (hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          {!success && !error && (
            <>
              <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-blue-100">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">กำลังยืนยันอีเมล...</h2>
              <p className="text-gray-600">กรุณารอสักครู่</p>
            </>
          )}

          {success && (
            <>
              <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-100">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">อีเมลของคุณถูกยืนยันแล้ว</h2>
              <p className="text-gray-600">กำลังนำคุณไปยังหน้าแรก...</p>
              <div className="mt-6">
                <div className="animate-pulse flex justify-center">
                  <div className="h-2 w-32 bg-blue-200 rounded"></div>
                </div>
              </div>
            </>
          )}

          {error && (
            <>
              <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-red-100">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">เกิดข้อผิดพลาด</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                ไปหน้าเข้าสู่ระบบ
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-yellow-100">
            <Mail className="w-12 h-12 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            ยืนยันอีเมลของคุณ
          </h1>
          {checkingPayment ? (
            <p className="text-gray-600 mb-2">
              กรุณายืนยันอีเมลของคุณก่อนเข้าใช้งานระบบ
            </p>
          ) : hasPendingPayment ? (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-2">
              <div className="flex items-center gap-2 text-blue-800">
                <Clock className="w-5 h-5" />
                <p className="text-sm font-semibold">
                  กรุณารอ Admin อนุมัติการชำระเงิน
                </p>
              </div>
              <p className="text-xs text-blue-700 mt-1 ml-7">
                หลังจาก Admin อนุมัติแล้ว กรุณายืนยันอีเมลเพื่อเข้าใช้งาน
              </p>
            </div>
          ) : (
            <p className="text-gray-600 mb-2">
              กรุณายืนยันอีเมลของคุณก่อนเข้าใช้งานระบบ
            </p>
          )}
          <div className="bg-blue-50 rounded-lg p-3 mt-4">
            <p className="text-sm text-blue-900 font-semibold">
              {auth.currentUser.email}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {success && (
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-800 font-semibold mb-1">ส่งอีเมลสำเร็จ!</p>
                <p className="text-green-700 text-sm">
                  เราได้ส่งอีเมลยืนยันไปที่อีเมลของคุณแล้ว กรุณาตรวจสอบกล่องจดหมาย (รวมถึงโฟลเดอร์ Spam)
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              📧 ตรวจสอบกล่องจดหมายของคุณและคลิกลิงก์ยืนยันในอีเมล
            </p>
            <p className="text-blue-700 text-xs mt-2">
              💡 หลังจากคลิกลิงก์แล้ว กรุณากดปุ่ม "รีเฟรชสถานะ" ด้านล่าง
            </p>
          </div>

          <button
            onClick={handleResendEmail}
            disabled={loading || countdown > 0}
            className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                กำลังส่ง...
              </>
            ) : countdown > 0 ? (
              <>
                <Mail className="w-5 h-5" />
                ส่งอีกครั้งใน {countdown} วินาที
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                ส่งอีเมลยืนยันอีกครั้ง
              </>
            )}
          </button>

          <button
            onClick={handleRefresh}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            รีเฟรชสถานะ
          </button>

          <button
            onClick={handleLogout}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            ออกจากระบบ
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            ไม่ได้รับอีเมล? ตรวจสอบโฟลเดอร์ Spam หรือ Junk Mail
          </p>
        </div>
      </div>
    </div>
  );
}
